import React, { useState, useEffect } from "react";
import "./Analytics.scss"; // Add CSS file for role switcher styling
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { useAuth } from "../../contexts/AuthContext";
import axiosInstance from "../../axiosInstance";
import AccessDenied from "../../Pages/AccessDenied";
import {
  Users,
  Building2,
  Calendar,
  Clock,
  Briefcase,
  UserCheck,
  FileText,
  TrendingUp,
  AlertCircle,
} from "lucide-react";

const CHART_COLORS = {
  primary: ["#3b82f6", "#60a5fa", "#93c5fd"],
  success: ["#10b981", "#34d399", "#6ee7b7"],
  warning: ["#f59e0b", "#fbbf24", "#fcd34d"],
  purple: ["#8b5cf6", "#a78bfa", "#c4b5fd"],
};

const StatsCard = ({ icon: Icon, title, value, color = "blue" }) => (
  <div className={`stats-card stats-card--${color}`}>
    <div className="stats-card__accent" aria-hidden="true" />
    <div className="stats-card__icon">
      <Icon size={20} />
    </div>
    <div className="stats-card__content">
      <h3 className="stats-card__title">{title}</h3>
    </div>
    <div className="stats-card__value">{value}</div>
  </div>
);

const ChartCard = ({ title, children, fullWidth = false }) => (
  <div className={`chart-card ${fullWidth ? "chart-card--full" : ""}`}>
    <div className="chart-card__header">
      <h3 className="chart-card__title">{title}</h3>
    </div>
    <div className="chart-card__content">{children}</div>
  </div>
);

const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="spinner"></div>
    <p>Loading...</p>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="error-message">
    <AlertCircle size={16} />
    <span>{message}</span>
  </div>
);

const AdminAnalytics = () => {
  const { getUserAuthorities } = useAuth();

  const availableRoles = getUserAuthorities();
  const allowedRoles = ["ROLE_HR", "ROLE_ADMIN"];
  const userAnalyticsRoles = availableRoles.filter((r) =>
    allowedRoles.includes(r),
  );

  const [selectedRole, setSelectedRole] = useState(userAnalyticsRoles[0] || "");
  const [organizationalData, setOrganizationalData] = useState({
    branches: null,
    departments: null,
  });
  const [workforceData, setWorkforceData] = useState({
    roles: null,
    officeTimings: null,
    workingDays: null,
  });
  const [timeOffData, setTimeOffData] = useState({
    holidays: null,
    leaveTypes: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasMultipleRoles = userAnalyticsRoles.length > 1;
  const isAdmin = selectedRole === "ROLE_ADMIN";
  const isHR = selectedRole === "ROLE_HR";

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);

        // Determine base URL based on role
        let baseUrl = null;
        if (isAdmin) {
          baseUrl = process.env.REACT_APP_API_BASE_URL;
        } else if (isHR) {
          baseUrl = process.env.REACT_APP_API_HR_URL;
        }

        const [
          branchRes,
          deptRes,
          roleRes,
          timingRes,
          workingDaysRes,
          holidayRes,
          leaveTypeRes,
        ] = await Promise.all([
          axiosInstance.get(`${baseUrl}/analytics/branch/report`),
          axiosInstance.get(`${baseUrl}/analytics/departments/report`),
          axiosInstance.get(`${baseUrl}/analytics/role/report`),
          axiosInstance.get(`${baseUrl}/analytics/office-timings/report`),
          axiosInstance.get(`${baseUrl}/analytics/working-days/report`),
          axiosInstance.get(`${baseUrl}/analytics/holiday/report`),
          axiosInstance.get(`${baseUrl}/leave-type/analytics/report`),
        ]);

        setOrganizationalData({
          branches: branchRes.data?.data,
          departments: deptRes.data?.data,
        });

        setWorkforceData({
          roles: roleRes.data?.data,
          officeTimings: timingRes.data?.data,
          workingDays: workingDaysRes.data?.data,
        });

        setTimeOffData({
          holidays: holidayRes.data?.data,
          leaveTypes: leaveTypeRes.data?.data,
        });

        setError(null);
      } catch (err) {
        setError("Failed to load analytics data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin || isHR) {
      fetchAllData();
    }
  }, [selectedRole]);

  // Check access control
  if (!userAnalyticsRoles.length) return <AccessDenied />;

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  const { branches, departments } = organizationalData;
  const { roles, officeTimings, workingDays } = workforceData;
  const { holidays, leaveTypes } = timeOffData;

  // Prepare chart data
  const branchData =
    branches?.branchListResponseDTOList?.map((branch) => ({
      name: branch.branchName,
      employees: branch.employeeCount || 0,
    })) || [];

  const deptData =
    departments?.departmentList?.map((dept) => ({
      name: dept.deptName,
      employees: dept.employeeAnalytics?.totalEmployeeCount || 0,
    })) || [];

  const combinedOrgData = [
    ...branchData.map((b) => ({ ...b, type: "Branch" })),
    ...deptData.map((d) => ({ ...d, type: "Department" })),
  ];

  const roleData =
    roles?.roleList?.map((role) => ({
      name: role.roleName,
      employees: role.employeeAnalytics?.totalEmployeeCount || 0,
    })) || [];

  const timingData =
    officeTimings?.officeTimingList?.map((timing) => ({
      name: timing.officeTimingName,
      employees: timing.totalEmployeeCount || 0,
    })) || [];

  const workingDaysData =
    workingDays?.workingDays?.map((day) => ({
      name: day.workingTypeName,
      employees: day.employeeAnalytics?.totalEmployeeCount || 0,
    })) || [];

  const holidayTypeData =
    holidays?.holidayType?.map((type) => ({
      name: type.holidayType,
      value: type.count,
    })) || [];

  const totalEmployees =
    departments?.departmentList?.reduce(
      (sum, dept) => sum + (dept.employeeAnalytics?.totalEmployeeCount || 0),
      0,
    ) || 0;

  return (
    <div className="overview-grid">
      {/* Organizational Section */}
      <div className="analytics-section">
        <div className="section-header">
          <Building2 size={28} />
          <h2>Organizational Structure</h2>
          {hasMultipleRoles && (
            <div className="role-switcher" style={{ marginLeft: "auto" }}>
              {userAnalyticsRoles.map((role) => {
                const formattedRole = role
                  .replace("ROLE_", "")
                  .toLowerCase()
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (char) => char.toUpperCase());

                return (
                  <button
                    key={role}
                    className={`role-toggle-btn ${
                      selectedRole === role ? "role-toggle-btn--active" : ""
                    }`}
                    onClick={() => setSelectedRole(role)}
                  >
                    {formattedRole}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="stats-grid stats-grid--compact">
          <StatsCard
            icon={Building2}
            title="Total Branches"
            value={branches?.totalBranchCount || 0}
            color="blue"
          />
          <StatsCard
            icon={Briefcase}
            title="Departments"
            value={departments?.totalDeptCount || 0}
            color="purple"
          />
          <StatsCard
            icon={Users}
            title="Total Employees"
            value={totalEmployees}
            color="green"
          />
        </div>

        <div className="charts-row">
          <ChartCard title="Branches Overview">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={branchData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Bar
                  dataKey="employees"
                  fill={CHART_COLORS.primary[0]}
                  name="Employees"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Departments Overview">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={deptData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Bar
                  dataKey="employees"
                  fill={CHART_COLORS.primary[1]}
                  name="Employees"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      {/* Workforce Section */}
      <div className="analytics-section">
        <div className="section-header">
          <Users size={28} />
          <h2>Workforce Management</h2>
        </div>

        <div className="stats-grid stats-grid--compact">
          <StatsCard
            icon={UserCheck}
            title="Total Roles"
            value={roles?.totalRoleCount || 0}
            color="purple"
          />
          <StatsCard
            icon={Clock}
            title="Office Timings"
            value={officeTimings?.totalCount || 0}
            color="orange"
          />
          <StatsCard
            icon={Calendar}
            title="Working Day Types"
            value={workingDays?.totalCount || 0}
            color="blue"
          />
        </div>

        <div className="charts-row">
          <ChartCard title="Employees by Role">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={roleData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Bar
                  dataKey="employees"
                  fill={CHART_COLORS.purple[1]}
                  name="Employees"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Office Timing Distribution">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={timingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Bar
                  dataKey="employees"
                  fill={CHART_COLORS.warning[1]}
                  name="Employees"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard title="Working Day Types" fullWidth>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={workingDaysData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="employees"
                stroke={CHART_COLORS.primary[0]}
                fill={CHART_COLORS.primary[1]}
                name="Employees"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Time Off Section */}
      <div className="analytics-section">
        <div className="section-header">
          <Calendar size={28} />
          <h2>Time Off Management</h2>
        </div>

        <div className="stats-grid stats-grid--compact">
          <StatsCard
            icon={Calendar}
            title="Total Holidays"
            value={holidays?.totalCount || 0}
            color="green"
          />
          <StatsCard
            icon={Calendar}
            title="Mandatory Holidays"
            value={
              holidays?.holidayType?.find((h) => h.holidayType === "MANDATORY")
                ?.count || 0
            }
            color="blue"
          />
          <StatsCard
            icon={Calendar}
            title="Optional Holidays"
            value={
              holidays?.holidayType?.find((h) => h.holidayType === "OPTIONAL")
                ?.count || 0
            }
            color="purple"
          />
          <StatsCard
            icon={FileText}
            title="Leave Types"
            value={leaveTypes?.totalCount || 0}
            color="orange"
          />
        </div>

        <ChartCard title="Holiday Types Distribution" fullWidth>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={holidayTypeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={100}
                dataKey="value"
              >
                {holidayTypeData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={CHART_COLORS.success[index]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

export default AdminAnalytics;
